import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, ChevronDown, ChevronRight, Menu, X, Book, Code, FileText, HelpCircle, Moon, Sun } from 'lucide-react';
import { getContent, extractHeadings } from './contentLoader';
import MarkdownContent from './components/MarkdownContent';
import 'highlight.js/styles/github-dark.css';

const App = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('quickstart');
  const [expandedSections, setExpandedSections] = useState({
    'api-guides': true,
    'shidonipay-101': false,
    'start-payments': false,
    'sandbox': false,
    'production': false
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);
  const searchInputRef = useRef(null);
  const contentRef = useRef(null);
  const [activeHeading, setActiveHeading] = useState('');
  const [currentContent, setCurrentContent] = useState(null);
  const [currentHeadings, setCurrentHeadings] = useState([]);

  // Searchable content
  const allSearchableContent = useMemo(() => [
    { id: 'quickstart', title: 'Quickstart', category: 'Getting Started', keywords: ['start', 'begin', 'first', 'api', 'key'] },
    { id: 'how-it-works', title: 'How ShidoniPay Works', category: 'ShidoniPay 101', keywords: ['overview', 'architecture', 'flow'] },
    { id: 'supported-assets', title: 'Assets ShidoniPay Supports', category: 'ShidoniPay 101', keywords: ['crypto', 'bitcoin', 'ethereum', 'currency'] },
    { id: 'glossary', title: 'Glossary', category: 'ShidoniPay 101', keywords: ['terms', 'definitions', 'vocabulary'] },
    { id: 'faq', title: 'Frequently Asked Questions', category: 'ShidoniPay 101', keywords: ['questions', 'help', 'common'] },
    { id: 'payment-lifecycle', title: 'Payment Lifecycle', category: 'Start with Payments', keywords: ['states', 'flow', 'process'] },
    { id: 'core-objects', title: 'Core Objects', category: 'Start with Payments', keywords: ['data', 'structure', 'models'] },
    { id: 'payment-object', title: 'Payment Object', category: 'Start with Payments', keywords: ['payment', 'object', 'structure'] },
    { id: 'payment-instructions', title: 'Payment Instructions', category: 'Start with Payments', keywords: ['payment', 'object', 'instructions'] },
    { id: 'webhooks', title: 'Webhooks', category: 'Start with Payments', keywords: ['notifications', 'events', 'callback'] },
    { id: 'testing-crypto', title: 'Testing Payments with Crypto', category: 'Sandbox Testing', keywords: ['test', 'sandbox', 'testnet'] },
    { id: 'testing-sandbox', title: 'Testing in Sandbox', category: 'Sandbox Testing', keywords: ['test', 'sandbox', 'environment'] },
    { id: 'error-handling', title: 'Error Handling', category: 'Sandbox Testing', keywords: ['errors', 'failures', 'exceptions'] },
    { id: 'go-live', title: 'Ready to Go Live', category: 'Getting Started', keywords: ['production', 'deploy', 'launch'] },
    { id: 'api-reference', title: 'API Overview', category: 'API Overview', keywords: ['endpoints', 'HTTP', 'reference'] },
    { id: 'oauth', title: 'OAuth 2.0', category: 'API Reference', keywords: ['authentication', 'auth', 'token'] },
    { id: 'payments', title: 'Payments API', category: 'API Reference', keywords: ['endpoint', 'create', 'retrieve'] },
    { id: 'webhook-endpoints', title: 'Webhook Endpoints', category: 'API Reference', keywords: ['webhook', 'endpoints', 'api'] },
    { id: 'sandbox-endpoints', title: 'Sandbox Endpoints', category: 'API Reference', keywords: ['sandbox', 'endpoints', 'api'] },
    { id: 'changelog', title: 'Changelog', category: 'Updates', keywords: ['changes', 'updates', 'releases'] },
    { id: 'help-center', title: 'Help Center', category: 'Help', keywords: ['help', 'support', 'billing'] },
    { id: 'integration-overview', title: 'Guided Integrations', category: 'Integrations', keywords: ['integration', 'support', 'guided'] },
    { id: 'sandbox-integration', title: 'Sandbox Integrations', category: 'Integrations', keywords: ['integration', 'support', 'sandbox'] },
    { id: 'production-integration', title: 'Production Integrations', category: 'Integrations', keywords: ['integration', 'support', 'production'] }

  ], []);

  const navigation = [
    {
      id: 'welcome',
      title: 'Welcome',
      icon: <Book size={18} />,
      items: [
        { id: 'quickstart', title: 'Quickstart' }
      ]
    },
    {
      id: 'api-guides',
      title: 'API Guides',
      icon: <FileText size={18} />,
      expandable: true,
      items: [
        {
          id: 'shidonipay-101',
          title: 'ShidoniPay 101',
          expandable: true,
          subitems: [
            { id: 'how-it-works', title: 'How ShidoniPay Works' },
            { id: 'supported-assets', title: 'Assets ShidoniPay Supports' },
            { id: 'glossary', title: 'Glossary' },
            { id: 'faq', title: 'Frequently Asked Questions' }
          ]
        },
        {
          id: 'start-payments',
          title: 'Start with Payments',
          expandable: true,
          subitems: [
            { id: 'payment-lifecycle', title: 'Payment Lifecycle' },
            { id: 'core-objects', title: 'Core Objects' },
            { id: 'payment-object', title: 'Payment Object' },
            { id: 'payment-instructions', title: 'Payment Instructions' },
            { id: 'webhooks', title: 'Webhooks' }
          ]
        },
        {
          id: 'sandbox',
          title: 'Sandbox Testing',
          expandable: true,
          subitems: [
            { id: 'testing-crypto', title: 'Testing Payments with Crypto' },
            { id: 'testing-sandbox', title: 'Testing in Sandbox' },
            { id: 'error-handling', title: 'Error Handling & Failure Modes' }
          ]
        },
        {
          id: 'production',
          title: 'Production',
          expandable: true,
          subitems: [
            { id: 'go-live', title: 'Ready to Go Live?' }
          ]
        },
        {
          id: 'guided-integration',
          title: 'Guided Integration',
          expandable: true,
          subitems: [
            { id: 'integration-overview', title: 'Integration Overview' },
            { id: 'sandbox-integration', title: 'Sandbox Integration' },
            { id: 'production-integration', title: 'Production Integration' }
          ]
        }
      ]
    },
    {
      id: 'api-reference-section',
      title: 'API Reference',
      icon: <Code size={18} />,
      items: [
        { id: 'api-reference', title: 'API Overview' },
        { id: 'oauth', title: 'OAuth 2.0' },
        { id: 'payments', title: 'Payments' },
        { id: 'webhook-endpoints', title: 'Webhook Endpoints' },
        { id: 'sandbox-endpoints', title: 'Sandbox Endpoints' }
      ]
    },
    {
      id: 'changelog',
      title: 'Changelog',
      icon: <FileText size={18} />,
      items: []
    },
    {
      id: 'help-center',
      title: 'Help Center',
      icon: <HelpCircle size={18} />,
      items: []
    }
  ];

  // Load markdown content when page changes
  useEffect(() => {
    const loadContent = async () => {
      const content = await getContent(activeSection);
      setCurrentContent(content);

      if (content && content.content) {
        const headings = extractHeadings(content.content);
        setCurrentHeadings(headings);
      } else {
        setCurrentHeadings([]);
      }
    };

    loadContent();
  }, [activeSection]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // ⌘K or Ctrl+K for search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
      // Escape to close search
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen]);

  // Search filtering
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const results = allSearchableContent.filter(item =>
        item.title.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query) ||
        item.keywords.some(keyword => keyword.includes(query))
      );
      setFilteredResults(results);
    } else {
      setFilteredResults([]);
    }
  }, [searchQuery, allSearchableContent]);

  // Scroll spy for TOC
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeading(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' }
    );

    const headings = contentRef.current?.querySelectorAll('h2[id]');
    headings?.forEach((heading) => observer.observe(heading));

    return () => observer.disconnect();
  }, [activeSection, currentContent]);

  // Auto-close sidebar on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleNavClick = (itemId, parentId) => {
    setActiveSection(itemId);

    // Auto-expand parent section if it's collapsed
    if (parentId) {
      setExpandedSections(prev => ({
        ...prev,
        [parentId]: true
      }));
    }

    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchResultClick = (itemId) => {
    setActiveSection(itemId);
    setSearchOpen(false);
    setSearchQuery('');

    // Close sidebar on mobile
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToHeading = (headingId) => {
    const element = document.getElementById(headingId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Helper function to determine which top nav section is active
  const getActiveTopNav = () => {
    const apiGuidesPages = [
      'how-it-works', 'supported-assets', 'glossary', 'faq',
      'payment-lifecycle', 'core-objects', 'payment-object', 'webhooks',
      'testing-crypto', 'testing-sandbox', 'error-handling', 'go-live', 'integration-overview',
      'sandbox-integration', 'production-integration'
    ];
    const apiReferencePages = [
      'api-reference', 'oauth', 'payments', 'webhook-endpoints', 'sandbox-endpoints'
    ];

    if (apiGuidesPages.includes(activeSection)) return 'api-guides';
    if (apiReferencePages.includes(activeSection)) return 'api-reference';
    if (activeSection === 'changelog') return 'changelog';
    if (activeSection === 'help-center') return 'help-center';
    return null;
  };

  const renderNavItem = (item, level = 0, parentId = null) => {
    const isExpanded = expandedSections[item.id];
    const hasSubitems = item.subitems && item.subitems.length > 0;
    const isActive = activeSection === item.id;

    return (
      <div key={item.id}>
        <div
          className={`flex items-center gap-2 px-4 py-2 cursor-pointer transition-colors ${isActive ? 'bg-blue-700 text-white' : 'text-blue-100 hover:bg-blue-700'
            } ${level > 0 ? 'pl-8' : ''}`}
          onClick={() => {
            if (hasSubitems) {
              toggleSection(item.id);
            } else {
              handleNavClick(item.id, parentId);
            }
          }}
        >
          {hasSubitems && (
            <span className="flex-shrink-0">
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          )}
          {!hasSubitems && level === 0 && <span className="w-4" />}
          {!hasSubitems && level > 0 && (
            <span className="flex-shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-300" />
            </span>
          )}
          <span className="text-sm font-medium">{item.title}</span>
        </div>
        {hasSubitems && isExpanded && (
          <div>
            {item.subitems.map(subitem => renderNavItem(subitem, level + 1, item.id))}
          </div>
        )}
      </div>
    );
  };

  const renderNavSection = (section) => {
    const isExpanded = expandedSections[section.id];
    const hasItems = section.items && section.items.length > 0;

    return (
      <div key={section.id} className="mb-2">
        <div
          className={`flex items-center gap-2 px-4 py-2 cursor-pointer text-blue-100 hover:bg-blue-700 transition-colors ${!hasItems && activeSection === section.id ? 'bg-blue-700' : ''
            }`}
          onClick={() => {
            if (hasItems) {
              toggleSection(section.id);
            } else {
              handleNavClick(section.id);
            }
          }}
        >
          {section.icon}
          <span className="text-sm font-semibold flex-1">{section.title}</span>
          {hasItems && section.expandable && (
            <span>
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          )}
        </div>
        {hasItems && (isExpanded || !section.expandable) && (
          <div>
            {section.items.map(item => renderNavItem(item, 0, section.id))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`flex h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} font-sans transition-colors`}>
      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20" onClick={() => setSearchOpen(false)}>
          <div className={`w-full max-w-2xl mx-4 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-2xl`} onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-11 pr-4 py-3 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} border-none focus:outline-none text-lg`}
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {filteredResults.length > 0 ? (
                <div className="py-2">
                  {filteredResults.map((result) => (
                    <div
                      key={result.id}
                      onClick={() => handleSearchResultClick(result.id)}
                      className={`px-4 py-3 cursor-pointer ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}
                    >
                      <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{result.title}</div>
                      <div className="text-sm text-gray-500">{result.category}</div>
                    </div>
                  ))}
                </div>
              ) : searchQuery ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  No results found for "{searchQuery}"
                </div>
              ) : (
                <div className="px-4 py-8 text-center text-gray-500">
                  Start typing to search...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`${sidebarOpen ? 'w-80' : 'w-0'
          } ${darkMode ? 'bg-gradient-to-b from-gray-800 to-gray-900' : 'bg-gradient-to-b from-blue-900 to-blue-800'} transition-all duration-300 overflow-hidden flex flex-col fixed lg:relative h-full z-40`}
      >
        {/* Logo */}
        <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-blue-700'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <div className={`${darkMode ? 'text-gray-800' : 'text-blue-900'} font-bold text-xl`}>S</div>
            </div>
            <div className="text-white text-xl font-semibold">ShidoniPay Docs</div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-4">
          {navigation.map(section => renderNavSection(section))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-4 lg:px-6 py-4 transition-colors`}>
          <div className="flex items-center gap-2 lg:gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-2 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-lg transition-colors flex-shrink-0`}
            >
              {sidebarOpen ? <X size={20} className={darkMode ? 'text-white' : ''} /> : <Menu size={20} className={darkMode ? 'text-white' : ''} />}
            </button>

            {/* Desktop Navigation Tabs */}
            <div className="hidden md:flex gap-6 flex-1">
              <button
                onClick={() => handleNavClick('how-it-works')}
                className={`text-sm font-medium ${getActiveTopNav() === 'api-guides'
                    ? darkMode ? 'text-blue-400 border-b-2 border-blue-400' : 'text-blue-600 border-b-2 border-blue-600'
                    : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                  } pb-1 transition-colors`}
              >
                API Guides
              </button>
              <button
                onClick={() => handleNavClick('api-reference')}
                className={`text-sm font-medium ${getActiveTopNav() === 'api-reference'
                    ? darkMode ? 'text-blue-400 border-b-2 border-blue-400' : 'text-blue-600 border-b-2 border-blue-600'
                    : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                  } pb-1 transition-colors`}
              >
                API Reference
              </button>
              <button
                onClick={() => handleNavClick('changelog')}
                className={`text-sm font-medium ${getActiveTopNav() === 'changelog'
                    ? darkMode ? 'text-blue-400 border-b-2 border-blue-400' : 'text-blue-600 border-b-2 border-blue-600'
                    : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                  } pb-1 transition-colors`}
              >
                Changelog
              </button>
              <button
                onClick={() => handleNavClick('help-center')}
                className={`text-sm font-medium ${getActiveTopNav() === 'help-center'
                    ? darkMode ? 'text-blue-400 border-b-2 border-blue-400' : 'text-blue-600 border-b-2 border-blue-600'
                    : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                  } pb-1 transition-colors`}
              >
                Help Center
              </button>
            </div>

            {/* Mobile: Just show current page name */}
            <div className="flex-1 md:hidden">
              <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'} truncate block`}>
                {currentContent?.title || 'Documentation'}
              </span>
            </div>

            {/* Dark Mode Toggle - Always visible */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-lg transition-colors flex-shrink-0`}
              title="Toggle dark mode"
            >
              {darkMode ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} />}
            </button>


            {/* Search Button - Always visible, icon only on mobile */}
            <button
              onClick={() => setSearchOpen(true)}
              className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} p-2 lg:px-4 lg:py-2 rounded-lg transition-colors flex items-center gap-2 flex-shrink-0 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
              title="Search (⌘K)"
            >
              <Search size={18} />
              <span className="hidden lg:inline text-sm">Search...</span>
              <kbd className={`hidden lg:inline px-2 py-1 text-xs ${darkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-100 border-gray-300'} border rounded`}>
                ⌘K
              </kbd>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex max-w-7xl mx-auto">
            {/* Main Content */}
            <div ref={contentRef} className="flex-1 px-8 py-12">
              {currentContent ? (
                <div className="prose prose-lg max-w-none">
                <MarkdownContent 
                  content={currentContent.content} 
                  darkMode={darkMode} 
                  onInternalLink={handleNavClick}
                />
              </div>
              ) : (
                <div className="prose prose-lg max-w-none">
                  <MarkdownContent 
                    content={`# Page Not Found\n\nThe content for "${activeSection}" couldn't be loaded.\n\n[Return to Quickstart](#quickstart)`} 
                    darkMode={darkMode} 
                    onInternalLink={handleNavClick}
                  />
                </div>
              )}
            </div>

            {/* Table of Contents - Right Sidebar */}
            {currentHeadings.length > 0 && (
              <div className="hidden xl:block w-64 py-12 pr-8">
                <div className="sticky top-12">
                  <h3 className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4 uppercase tracking-wide`}>
                    On This Page
                  </h3>
                  <nav className="space-y-2">
                    {currentHeadings.map((heading) => (
                      <button
                        key={heading.id}
                        onClick={() => scrollToHeading(heading.id)}
                        className={`block text-sm text-left w-full py-1 transition-colors ${activeHeading === heading.id
                          ? darkMode
                            ? 'text-blue-400 font-medium'
                            : 'text-blue-600 font-medium'
                          : darkMode
                            ? 'text-gray-500 hover:text-gray-300'
                            : 'text-gray-600 hover:text-gray-900'
                          }`}
                      >
                        {heading.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;